import styled from 'styled-components';

import { LayoutProps, layout, DisplayProps, display, MarginProps, margin, compose } from 'styled-system';

type VoteButtonProps = {
  highlightColor: string;
  children?: React.ReactNode;
  selected: boolean;
  blocked: boolean;
  onVote?: () => void;
  tabIndex?: number;
  title?: string;
  rotate?: number;
} & MarginProps;

/**
 * @param highlightColor: color con el que se resalta el
 *  botón al hacer mouseover o luego de votar.
 * @param children: icono a mostrar.
 * @param selected: indica si este botón fue seleccionado(votado).
 * @param blocked: se puede bloquear la posibilidad de votar
 * (por ejemplo si ya envió su voto).
 * @param onVote: callback que es llamado en caso de que se
 * presione este botón para votar y no esté bloqueado.
 */
export function VoteButton(props: VoteButtonProps) {
  const { highlightColor, children, selected, blocked, onVote, title, rotate, ...otherProps } = props;

  function processVote() {
    if (selected || blocked) return;

    onVote?.();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key === 'Enter') processVote();
  }

  return (
    <VoteButtonContainer
      rotate={props.rotate}
      width={32}
      color="secondary.1"
      highlightColor={highlightColor}
      selected={selected}
      blocked={blocked}
      onClick={processVote}
      onKeyDown={onKeyDown}
      aria-label={title}
      role="link"
      {...otherProps}
    >
      {children}
    </VoteButtonContainer>
  );
}

type VoteButtonContainerProps = {
  color: string;
  highlightColor: string;
  selected: boolean;
  blocked: boolean;
  rotate?: number;
} & LayoutProps &
  DisplayProps &
  MarginProps;

const VoteButtonContainer = styled.div<VoteButtonContainerProps>(
  compose(layout, display, margin),
  (props) => ({
    cursor: props.blocked ? 'not-allowed' : 'pointer',
    svg: {
      fill: props.selected ? props.highlightColor : props.color,
      stroke: props.selected ? props.highlightColor : props.color,
      opacity: props.selected ? 1 : 0.5,
      strokeWidth: 0,
      transform: `rotate(${props.rotate}deg)`,
    },
    'svg:hover': {
      fill: props.blocked ? '' : props.highlightColor,
      stroke: props.blocked ? '' : props.highlightColor,
    },
  }),
  {
    '.stroke': {
      fill: 'none',
      strokeWidth: '1px',
    },
  }
);
